import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult } from '@/utils/sajuCalculations';
import {
  analyzeSipseong,
  type SipseongAnalysis,
  type Sipseong,
  SIPSEONG_MAP,
  SIPSEONG_STORY,
} from '@/utils/sipseongCalculations';

interface Props {
  result: SajuResult;
}

// ─── Category meta (bilingual) ───────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, {
  label: string; desc: string; icon: string;
  enLabel: string; enDesc: string;
}> = {
  bijep:     { label: '비겁(比劫)', desc: '나 자신의 기운 — 자아와 독립심', icon: 'ri-user-3-line',                enLabel: 'Companion / Rob Wealth', enDesc: 'The self energy — ego and independence' },
  siksang:   { label: '식상(食傷)', desc: '표현과 창의의 기운 — 재능과 감성', icon: 'ri-brush-line',               enLabel: 'Eating God / Hurt Officer', enDesc: 'Expression and creativity — talent and emotion' },
  jaeseong:  { label: '재성(財星)', desc: '재물과 사업의 기운 — 돈과 실리', icon: 'ri-money-dollar-circle-line',  enLabel: 'Indirect / Direct Wealth', enDesc: 'Wealth and business energy — money and practicality' },
  gwanseong: { label: '관성(官星)', desc: '사회적 역할의 기운 — 명예와 규율', icon: 'ri-shield-star-line',         enLabel: '7 Killings / Direct Officer', enDesc: 'Social role energy — status and discipline' },
  inseong:   { label: '인성(印星)', desc: '학문과 지혜의 기운 — 지식과 보호', icon: 'ri-book-2-line',              enLabel: 'Indirect / Direct Resource', enDesc: 'Knowledge and wisdom energy — learning and protection' },
};

// ─── Story tab definitions ────────────────────────────────────────────────────
const STORY_TABS = [
  { id: 'career', icon: 'ri-briefcase-4-line', labelKey: 'sipseong.tab.career' },
  { id: 'love',   icon: 'ri-heart-3-line',     labelKey: 'sipseong.tab.love' },
  { id: 'wealth', icon: 'ri-safe-2-line',       labelKey: 'sipseong.tab.wealth' },
] as const;
type StoryTab = typeof STORY_TABS[number]['id'];

// ─── SipseongBadge ───────────────────────────────────────────────────────────
function SipseongBadge({ ss, count }: { ss: Sipseong; count: number }) {
  const { i18n } = useTranslation();
  const displayName = i18n.language === 'en' ? ss.enName : ss.name;
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl" style={{ backgroundColor: ss.bg }}>
      <span className="text-xs font-bold" style={{ color: ss.color }}>{displayName}</span>
      <span className="text-xs font-semibold w-4 h-4 rounded-full flex items-center justify-center bg-white/70" style={{ color: ss.color }}>{count}</span>
    </div>
  );
}

// ─── HiddenGemCard ───────────────────────────────────────────────────────────
function HiddenGemCard({ analysis }: { analysis: SipseongAnalysis }) {
  const { t, i18n } = useTranslation();
  const { hidden } = analysis;
  const isEn = i18n.language === 'en';
  const topHidden = Object.entries(analysis.hiddenCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="rounded-2xl p-4 border border-dashed" style={{ borderColor: hidden.color + '50', backgroundColor: hidden.bg }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ backgroundColor: hidden.color }}>
          <i className="ri-eye-close-line text-white text-xs" />
        </div>
        <p className="text-xs font-bold text-gray-700">{t('sipseong.hidden.title')}</p>
      </div>
      <p className="text-sm font-bold mb-1" style={{ color: hidden.color }}>
        {t('sipseong.hidden.label')}'{isEn ? hidden.enName : hidden.kor}'
      </p>
      <p className="text-xs text-gray-500 leading-relaxed mb-2.5">
        {isEn ? hidden.enKeyword : hidden.keyword} — {hidden.description}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {topHidden.map(([name]) => {
          const s = SIPSEONG_MAP[name];
          if (!s) return null;
          return (
            <span key={name} className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: s.color, color: '#fff' }}>
              {isEn ? s.enName : s.name}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── StoryCards ──────────────────────────────────────────────────────────────
function StoryCards({ dominant }: { dominant: Sipseong }) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<StoryTab>('career');
  const story = SIPSEONG_STORY[dominant.name];
  if (!story) return null;

  const isEn = i18n.language === 'en';

  const content = {
    career: {
      headline: isEn ? story.career.headlineEn : story.career.headline,
      detail: isEn ? story.career.detailEn : story.career.detail,
      extra: (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(isEn ? story.career.jobsEn : story.career.jobs).map(j => (
            <span key={j} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: dominant.color + '15', color: dominant.color }}>
              {j}
            </span>
          ))}
        </div>
      ),
    },
    love: {
      headline: isEn ? story.love.headlineEn : story.love.headline,
      detail: isEn ? story.love.detailEn : story.love.detail,
      extra: (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: dominant.color + '10' }}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-heart-line text-xs" style={{ color: dominant.color }} />
          </div>
          <span className="text-xs font-bold" style={{ color: dominant.color }}>
            {isEn ? story.love.styleEn : story.love.style}
          </span>
        </div>
      ),
    },
    wealth: {
      headline: isEn ? story.wealth.headlineEn : story.wealth.headline,
      detail: isEn ? story.wealth.detailEn : story.wealth.detail,
      extra: (
        <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-xl" style={{ backgroundColor: dominant.color + '10' }}>
          <div className="w-4 h-4 flex items-center justify-center mt-0.5">
            <i className="ri-lightbulb-line text-xs" style={{ color: dominant.color }} />
          </div>
          <span className="text-xs leading-relaxed" style={{ color: dominant.color }}>
            {isEn ? story.wealth.tipEn : story.wealth.tip}
          </span>
        </div>
      ),
    },
  };

  const current = content[activeTab];

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-100 mb-4">
      {/* 탭 헤더 */}
      <div className="flex bg-gray-50">
        {STORY_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 text-xs font-bold transition-all cursor-pointer whitespace-nowrap border-b-2 ${
              activeTab === tab.id
                ? 'border-gray-900 bg-white text-gray-900'
                : 'border-transparent text-gray-400'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${tab.icon} text-sm`} />
            </div>
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      <div className="p-4 bg-white">
        <p className="text-sm font-bold text-gray-900 mb-1.5">{current.headline}</p>
        <p className="text-xs text-gray-600 leading-relaxed">{current.detail}</p>
        {current.extra}
      </div>
    </div>
  );
}

// ─── Main SipseongSection ─────────────────────────────────────────────────────
export default function SipseongSection({ result }: Props) {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isEn = i18n.language === 'en';
  const analysis: SipseongAnalysis = analyzeSipseong(result);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { dominant } = analysis;
  const story = SIPSEONG_STORY[dominant.name];

  const categoryCounts: Record<string, number> = {};
  for (const [name, count] of Object.entries(analysis.allCounts)) {
    const cat = SIPSEONG_MAP[name]?.category || 'bijep';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + count;
  }
  const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];

  const catMeta = topCategory ? CATEGORY_LABELS[topCategory[0]] : null;

  return (
    <div
      ref={ref}
      className="bg-white border border-gray-100 rounded-2xl p-5 mb-6 transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
    >
      {/* 헤더 */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
          <i className="ri-star-line text-amber-500 text-sm" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">{t('sipseong.title')}</h3>
          <p className="text-xs text-gray-400">{t('sipseong.subtitle')}</p>
        </div>
      </div>

      {/* 대표 십성 메인 카드 */}
      <div
        className="rounded-2xl p-4 mb-5"
        style={{ background: `linear-gradient(135deg, ${dominant.bg} 0%, ${dominant.bg}99 100%)` }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <span
              className="text-xs font-bold px-2 py-0.5 rounded-full mb-2 inline-block text-white"
              style={{ backgroundColor: dominant.color }}
            >
              {t('sipseong.dominant')}
            </span>
            <h4
              className="text-2xl font-black mt-1"
              style={{ color: dominant.color, fontFamily: isEn ? 'inherit' : "'Noto Serif KR', serif" }}
            >
              {isEn ? dominant.enName : dominant.kor}
            </h4>
            {isEn && (
              <p className="text-xs mt-0.5 font-medium" style={{ color: dominant.color + 'aa' }}>
                {dominant.kor}
              </p>
            )}
          </div>
          <div
            className="text-4xl font-black opacity-15 flex-none leading-none"
            style={{ color: dominant.color, fontFamily: "'Noto Serif KR', serif" }}
          >
            {dominant.name}
          </div>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: dominant.color + '20', color: dominant.color }}
          >
            {isEn ? dominant.enKeyword : dominant.keyword}
          </span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed">{dominant.description}</p>
      </div>

      {/* 커리어·연애·금전 스토리 카드 (탭) */}
      <StoryCards dominant={dominant} />

      {/* 조언 카드 */}
      {story && (
        <div className="bg-amber-50 rounded-2xl p-4 mb-4 flex items-start gap-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-xl bg-amber-400 flex-none mt-0.5">
            <i className="ri-message-3-line text-white text-xs" />
          </div>
          <div>
            <p className="text-xs font-bold text-amber-800 mb-1">{t('sipseong.advice.title')}</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              {isEn ? story.adviceEn : story.advice}
            </p>
          </div>
        </div>
      )}

      {/* 카테고리 기운 */}
      {catMeta && (
        <div className="bg-gray-50 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${catMeta.icon} text-gray-500 text-sm`} />
            </div>
            <p className="text-xs font-bold text-gray-700">
              {isEn ? catMeta.enLabel : catMeta.label}
            </p>
          </div>
          <p className="text-xs text-gray-500">{isEn ? catMeta.enDesc : catMeta.desc}</p>
        </div>
      )}

      {/* 십성 분포 뱃지 */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {Object.entries(analysis.allCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => {
            const ss = SIPSEONG_MAP[name];
            if (!ss) return null;
            return <SipseongBadge key={name} ss={ss} count={count} />;
          })}
      </div>

      {/* 지장간 숨은 기운 */}
      <HiddenGemCard analysis={analysis} />

      {/* 각 기둥 상세 보기 토글 */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full mt-4 py-2.5 text-xs font-medium text-gray-500 bg-gray-50 rounded-xl flex items-center justify-center gap-1.5 cursor-pointer hover:bg-gray-100 transition-all whitespace-nowrap"
      >
        {expanded ? t('collapse') : t('sipseong.detail.title')}
        {expanded
          ? <i className="ri-arrow-up-s-line" />
          : <i className="ri-arrow-down-s-line" />
        }
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {analysis.pillars.map(p => (
            <div key={p.pillarLabel} className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs font-bold text-gray-700 mb-2">{p.pillarLabel}</p>
              <div className="space-y-1.5">
                {p.stemSipseong ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-10 flex-shrink-0">{t('sipseong.detail.stem')}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: p.stemSipseong.bg, color: p.stemSipseong.color }}>
                      {isEn ? p.stemSipseong.enName : p.stemSipseong.name}
                    </span>
                    <span className="text-xs text-gray-500">{isEn ? p.stemSipseong.enKeyword : p.stemSipseong.keyword}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-10 flex-shrink-0">{t('sipseong.detail.stem')}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">{t('sipseong.detail.daegan')}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-10 flex-shrink-0">{t('sipseong.detail.branch')}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: p.branchSipseong.bg, color: p.branchSipseong.color }}>
                    {isEn ? p.branchSipseong.enName : p.branchSipseong.name}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-gray-200">
                  <span className="text-xs text-gray-400 mr-0.5">{t('sipseong.detail.jijang')}:</span>
                  {p.jijang.map((j, i) => (
                    <span key={i} className="text-xs px-1.5 py-0.5 rounded-md" style={{ backgroundColor: j.sipseong.bg, color: j.sipseong.color }}>
                      {j.stemName}({isEn ? j.sipseong.enName : j.sipseong.name})
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
